/**
 * @fileoverview Badges Card for Profile
 * 
 * Shows streak counter at top, then a 3-column grid of 12 badges.
 * Unlocked badges show gradient + editorial texture overlay + white icon.
 * Locked badges show grey background with SVG progress ring.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, ArrowRight, HelpCircle, ChevronDown } from 'lucide-react';
import { useBadgeProgress, BadgeProgress, type BadgeDefinition, type TextureKey } from '@/hooks/useBadges';
import { useEngagementStats } from '@/hooks/useEngagementStats';
import { BadgeDetailSheet } from '@/components/profile/BadgeDetailSheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Maps a badge's statKey to the route where the user can make progress */
function getBadgeRoute(statKey: BadgeDefinition['statKey']): string {
  switch (statKey) {
    case 'saves': return '/';
    case 'collections': return '/';
    case 'votes': return '/vote';
    case 'polls': return '/vote';
    case 'tryons': return '/studio';
    case 'extension': return '/profile/settings';
    case 'streak': return '/';
    case 'spring_cleans': return '/wardrobe';
    case 'price_guess_games':
    case 'price_guess_perfect':
    case 'price_guess_level':
      return '/price-guess';
    default: return '/';
  }
}

/** Returns an action prompt for the badge */
function getBadgePrompt(badge: BadgeDefinition): string {
  switch (badge.statKey) {
    case 'saves': return 'Save your first item to unlock!';
    case 'collections': return 'Create a collection to unlock!';
    case 'votes': return 'Vote on a poll to unlock!';
    case 'tryons': return 'Try on a look to unlock!';
    case 'extension': return 'Enable the Safari extension!';
    case 'streak': return 'Use the app daily to build a streak!';
    case 'spring_cleans': return 'Clean out your wardrobe to unlock!';
    case 'price_guess_games': return 'Play a Price Guess to unlock!';
    default: return `${badge.description} to unlock!`;
  }
}

interface BadgesCardProps {
  userId?: string;
}

/** Returns a CSS backgroundImage string for editorial texture overlays */
export function getTextureStyle(texture: TextureKey): string {
  switch (texture) {
    case 'linen':
      return `repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 4px)`;
    case 'grid':
      return `radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)`;
    case 'canvas':
      return `repeating-linear-gradient(45deg, rgba(255,255,255,0.07) 0px, transparent 1px, transparent 3px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.07) 0px, transparent 1px, transparent 3px)`;
    case 'paper':
      return `radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px), radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)`;
    case 'mosaic':
      return `repeating-linear-gradient(0deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 6px), repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 6px)`;
    case 'silk':
      return `linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 100%)`;
    case 'brushstroke':
      return `radial-gradient(ellipse 120% 40% at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 100%), radial-gradient(ellipse 100% 30% at 80% 60%, rgba(255,255,255,0.08) 0%, transparent 100%)`;
    case 'ember':
      return `radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 40%, transparent 70%)`;
    case 'marble':
      return `radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.12) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)`;
    case 'knit':
      return `repeating-linear-gradient(60deg, rgba(255,255,255,0.06) 0px, transparent 1px, transparent 3px), repeating-linear-gradient(-60deg, rgba(255,255,255,0.06) 0px, transparent 1px, transparent 3px)`;
    case 'sand':
      return `radial-gradient(circle 1px, rgba(255,255,255,0.1) 1px, transparent 1px)`;
    case 'foil':
      return `linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.2) 75%, rgba(255,255,255,0) 100%)`;
    default:
      return 'none';
  }
}

function getTextureBackgroundSize(texture: TextureKey): string | undefined {
  switch (texture) {
    case 'grid': return '6px 6px';
    case 'paper': return '5px 5px, 7px 7px';
    case 'sand': return '4px 4px';
    default: return undefined;
  }
}

function BadgeCircle({ bp, onClick }: { bp: BadgeProgress; onClick: () => void }) {
  const { badge, earned, progress } = bp;
  const Icon = badge.icon;
  const almostThere = !earned && progress >= 80;

  // SVG progress ring params
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group focus:outline-none"
    >
      <div className="relative w-16 h-16">
        {/* Progress ring for locked badges */}
        {!earned && progress > 0 && (
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 80 80">
            <circle
              cx="40" cy="40" r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="text-muted/50"
            />
            <circle
              cx="40" cy="40" r={radius}
              fill="none"
              strokeWidth="3"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              stroke={`url(#gradient-${badge.id})`}
              className={cn(almostThere && 'animate-pulse')}
            />
            <defs>
              <linearGradient id={`gradient-${badge.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={badge.gradientFrom} />
                <stop offset="100%" stopColor={badge.gradientTo} />
              </linearGradient>
            </defs>
          </svg>
        )}

        {/* Badge circle */}
        <div
          className={cn(
            'absolute inset-1 rounded-full flex items-center justify-center transition-transform group-active:scale-95 overflow-hidden',
            earned ? 'shadow-md' : 'bg-muted'
          )}
          style={earned ? {
            background: `linear-gradient(135deg, ${badge.gradientFrom}, ${badge.gradientTo})`,
          } : undefined}
        >
          {/* Texture overlay for earned badges */}
          {earned && (
            <div
              className="absolute inset-0 rounded-full"
              style={{
                backgroundImage: getTextureStyle(badge.texture),
                backgroundSize: getTextureBackgroundSize(badge.texture),
              }}
            />
          )}
          <Icon className={cn(
            'w-6 h-6 relative z-10',
            earned ? 'text-white drop-shadow-sm' : 'text-muted-foreground/40'
          )} />
        </div>

        {/* Almost there dot */}
        {almostThere && (
          <div
            className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background animate-pulse"
            style={{ background: badge.gradientFrom }}
          />
        )}
      </div>
      <span className={cn(
        'text-[11px] leading-tight text-center max-w-[72px] line-clamp-2',
        earned ? 'text-foreground font-medium' : 'text-muted-foreground'
      )}>
        {badge.name}
      </span>
    </button>
  );
}

export function BadgesCard({ userId }: BadgesCardProps) {
  const { badgeProgress: rawBadgeProgress, earnedCount, totalBadges } = useBadgeProgress(userId);
  const { data: engagementStats, isLoading } = useEngagementStats(userId);
  const [selectedBadge, setSelectedBadge] = useState<BadgeProgress | null>(null);
  const [badgesExpanded, setBadgesExpanded] = useState(false);
  const navigate = useNavigate();

  // Sort: earned badges newest-first, then unearned by progress descending
  const badgeProgress = useMemo(() => {
    const earned = rawBadgeProgress.filter(bp => bp.earned)
      .sort((a, b) => new Date(b.earnedAt || 0).getTime() - new Date(a.earnedAt || 0).getTime());
    const unearned = rawBadgeProgress.filter(bp => !bp.earned)
      .sort((a, b) => b.progress - a.progress);
    return [...earned, ...unearned];
  }, [rawBadgeProgress]);

  const currentStreak = engagementStats?.currentStreak ?? 0;
  const longestStreak = engagementStats?.longestStreak ?? 0;

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  // Pick the "next up" badge: highest progress among unearned, default to first
  const nextBadge = earnedCount === 0
    ? badgeProgress.reduce<BadgeProgress | null>((best, bp) => {
        if (bp.earned) return best;
        if (!best || bp.progress > best.progress) return bp;
        return best;
      }, null) ?? badgeProgress[0]
    : null;

  return (
    <>
      <div>
        {/* Streak header */}
        <div className="px-4 py-3 flex items-center justify-between rounded-xl bg-muted/50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-full bg-orange-500/10">
              {currentStreak === 0 ? (
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <Flame className="w-4 h-4 text-orange-400" />
                </motion.div>
              ) : (
                <Flame className="w-4 h-4 text-orange-500" />
              )}
            </div>
            <div>
              <span className="text-sm font-semibold text-foreground">
                {currentStreak === 0 ? 'Start your streak' : `${currentStreak} day streak`}
              </span>
              {longestStreak > currentStreak && longestStreak > 0 && (
                <span className="text-xs text-muted-foreground ml-2">
                  Best: {longestStreak}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Next-up nudge for zero-badge users */}
        {nextBadge && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mx-4 mt-4 p-3 rounded-xl border border-border"
            style={{
              background: `linear-gradient(135deg, ${nextBadge.badge.gradientFrom}10, ${nextBadge.badge.gradientTo}10)`,
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${nextBadge.badge.gradientFrom}, ${nextBadge.badge.gradientTo})`,
                }}
              >
                <nextBadge.badge.icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{nextBadge.badge.name}</p>
                <p className="text-xs text-muted-foreground">{getBadgePrompt(nextBadge.badge)}</p>
              </div>
              <button
                onClick={() => navigate(getBadgeRoute(nextBadge.badge.statKey))}
                className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                style={{
                  background: `linear-gradient(135deg, ${nextBadge.badge.gradientFrom}, ${nextBadge.badge.gradientTo})`,
                }}
              >
                Let's go
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Badges section */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Your Badges</h3>
            <span className="text-xs text-muted-foreground">
              {earnedCount} / {totalBadges}
            </span>
          </div>

          {/* Always show top 3 */}
          <div className="grid grid-cols-3 gap-y-4 gap-x-2 justify-items-center">
            {badgeProgress.slice(0, 3).map((bp) => (
              <BadgeCircle
                key={bp.badge.id}
                bp={bp}
                onClick={() => setSelectedBadge(bp)}
              />
            ))}
          </div>

          {/* Expandable rest */}
          {badgeProgress.length > 3 && (
            <>
              <motion.div
                initial={false}
                animate={{
                  height: badgesExpanded ? 'auto' : 0,
                  opacity: badgesExpanded ? 1 : 0,
                }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-3 gap-y-4 gap-x-2 justify-items-center mt-4">
                  {badgeProgress.slice(3).map((bp) => (
                    <BadgeCircle
                      key={bp.badge.id}
                      bp={bp}
                      onClick={() => setSelectedBadge(bp)}
                    />
                  ))}
                  {/* Mystery badge to fill the grid */}
                  {(badgeProgress.length - 3) % 3 !== 0 && Array.from({ length: 3 - ((badgeProgress.length - 3) % 3) }).map((_, i) => (
                    <div key={`mystery-${i}`} className="flex flex-col items-center gap-1.5">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-1 rounded-full bg-muted flex items-center justify-center">
                          <HelpCircle className="w-6 h-6 text-muted-foreground/40" />
                        </div>
                      </div>
                      <span className="text-[11px] leading-tight text-center max-w-[72px] text-muted-foreground/40">Coming Soon</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <button
                onClick={() => setBadgesExpanded(prev => !prev)}
                className="w-full mt-3 flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                {badgesExpanded ? 'See fewer' : 'See all badges'}
                <motion.div
                  animate={{ rotate: badgesExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </button>
            </>
          )}
        </div>
      </div>

      <BadgeDetailSheet
        badge={selectedBadge}
        open={!!selectedBadge}
        onOpenChange={(open) => !open && setSelectedBadge(null)}
      />
    </>
  );
}
